import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthProvider';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  requireOrg?: boolean;
  requireSystem?: boolean;
  requireAdminForNonPipelet?: boolean;
}

export function ProtectedRoute({ children, requireAuth = true, requireOrg = false, requireSystem = false, requireAdminForNonPipelet = false }: ProtectedRouteProps) {
  const { user, activeOrganization, activeRole } = useAuth();
  const location = useLocation();

  // Not authenticated -> go to login, preserving intended destination
  if (requireAuth && !user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // Authenticated but on login page -> go to previous location or default route
  if (!requireAuth && user) {
    const from = (location.state as { from?: string })?.from;
    return <Navigate to={from || (activeOrganization ? '/team' : '/welcome')} replace />;
  }

  // Authenticated with no org but org is required -> go to welcome
  if (requireOrg && user && !activeOrganization) {
    return <Navigate to="/welcome" replace />;
  }

  // Only redirect from welcome page if user has an org
  if (location.pathname === '/welcome' && user && activeOrganization) {
    return <Navigate to="/team" replace />;
  }

  // Authenticated but not a system user -> go to team
  if (requireSystem && activeOrganization?.id !== 1) {
    return <Navigate to="/team" replace />;
  }

  // Require admin role for non-pipelet organizations
  if (requireAdminForNonPipelet && activeOrganization?.id !== 1 && activeRole !== 'admin') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
